import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import QRCode from 'qrcode';
import { type SyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Field } from '../components/ui/field';
import { Input } from '../components/ui/input';
import { api, errorMessage, setCsrfToken } from '../lib/api';
import { meQuery } from '../lib/session';
import type { LoginResponse } from '../lib/types';

type Step =
  | { kind: 'password' }
  | { kind: 'setup'; secret: string; qr: string }
  | { kind: 'verify' }
  | { kind: 'recovery'; codes: string[] };

/** Вход сотрудника: пароль → настройка 2FA (первый вход) или код → резервные коды. */
export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>({ kind: 'password' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Новый вход — старые данные сессии больше не нужны.
    queryClient.removeQueries({ queryKey: meQuery.queryKey });
  }, [queryClient]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(undefined);
    try {
      await action();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const enter = async () => {
    await queryClient.query(meQuery);
    await navigate({ to: '/' });
  };

  const submitPassword = (e: SyntheticEvent) => {
    e.preventDefault();
    void run(async () => {
      const result = await api<LoginResponse>('/auth/login', {
        method: 'POST',
        body: { email, password },
      });
      setCsrfToken(result.csrfToken);
      setPassword('');
      if (result.mfa === 'setup') {
        const setup = await api<{ secret: string; otpauthUri: string }>('/auth/mfa/setup', {
          method: 'POST',
        });
        const qr = await QRCode.toDataURL(setup.otpauthUri, { margin: 1, width: 220 });
        setStep({ kind: 'setup', secret: setup.secret, qr });
      } else {
        setStep({ kind: 'verify' });
      }
    });
  };

  const submitCode = (e: SyntheticEvent) => {
    e.preventDefault();
    void run(async () => {
      if (step.kind === 'setup') {
        const result = await api<{ recoveryCodes: string[] }>('/auth/mfa/confirm', {
          method: 'POST',
          body: { code },
        });
        setStep({ kind: 'recovery', codes: result.recoveryCodes });
      } else {
        const result = await api<{ usedRecoveryCode: boolean }>('/auth/mfa/verify', {
          method: 'POST',
          body: { code },
        });
        if (result.usedRecoveryCode) setNotice(t('auth.usedRecoveryCode'));
        await enter();
      }
      setCode('');
    });
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-sm">
        {step.kind === 'password' ? (
          <>
            <CardHeader title={t('auth.title')} />
            <CardContent>
              <form onSubmit={submitPassword} className="grid gap-4">
                <Field label={t('auth.email')}>
                  {(id) => (
                    <Input
                      id={id}
                      type="email"
                      autoComplete="username"
                      required
                      autoFocus
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                      }}
                    />
                  )}
                </Field>
                <Field label={t('auth.password')}>
                  {(id) => (
                    <Input
                      id={id}
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                      }}
                    />
                  )}
                </Field>
                {error ? <Alert variant="destructive">{error}</Alert> : null}
                <Button type="submit" disabled={busy}>
                  {t('auth.signIn')}
                </Button>
              </form>
            </CardContent>
          </>
        ) : step.kind === 'recovery' ? (
          <>
            <CardHeader title={t('auth.recoveryTitle')} description={t('auth.recoveryHint')} />
            <CardContent className="grid gap-4">
              <ul
                className="grid grid-cols-2 gap-2 rounded-md bg-muted p-3 font-mono text-sm"
                aria-label={t('auth.recoveryTitle')}
              >
                {step.codes.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
              {error ? <Alert variant="destructive">{error}</Alert> : null}
              <Button disabled={busy} onClick={() => void run(enter)}>
                {t('auth.recoverySaved')}
              </Button>
            </CardContent>
          </>
        ) : (
          <>
            <CardHeader
              title={step.kind === 'setup' ? t('auth.setupTitle') : t('auth.verifyTitle')}
              description={step.kind === 'setup' ? t('auth.setupHint') : t('auth.verifyHint')}
            />
            <CardContent>
              <form onSubmit={submitCode} className="grid gap-4">
                {step.kind === 'setup' ? (
                  <div className="grid justify-items-center gap-2">
                    <img src={step.qr} alt="QR" width={220} height={220} />
                    <p className="text-xs text-muted-foreground">{t('auth.secret')}</p>
                    <code className="break-all rounded bg-muted px-2 py-1 text-xs">
                      {step.secret}
                    </code>
                  </div>
                ) : null}
                <Field label={t('auth.code')}>
                  {(id) => (
                    <Input
                      id={id}
                      autoComplete="one-time-code"
                      inputMode={step.kind === 'setup' ? 'numeric' : 'text'}
                      required
                      autoFocus
                      value={code}
                      onChange={(e) => {
                        setCode(e.target.value.trim());
                      }}
                    />
                  )}
                </Field>
                {notice ? <Alert>{notice}</Alert> : null}
                {error ? <Alert variant="destructive">{error}</Alert> : null}
                <Button type="submit" disabled={busy}>
                  {t('common.confirm')}
                </Button>
              </form>
            </CardContent>
          </>
        )}
      </Card>
    </main>
  );
}
